import type { UploadFile, UploadProps } from 'antd'
import { BaseAvatar } from 'app/components/common/BaseAvatar'
import { BaseImage } from 'app/components/common/BaseImage'
import BaseText from 'app/components/common/BaseText'
import { Loading } from 'app/components/common/Loading'
import { SpaceVertical, Text } from 'app/components/styles'
import { memo, useEffect, useRef, useState } from 'react'
import { CSSProperties } from 'styled-components'
import * as S from './styles'
export const getImageBase64URL = (file): Promise<string | null> => {
  if (!file) return Promise.resolve(null)

  return new Promise(resolve => {
    const reader = new FileReader()

    reader.onload = () => {
      const base64Image = reader.result as string
      resolve(base64Image)
    }

    reader.onerror = () => resolve(null)

    reader.readAsDataURL(file)
  })
}

const UploadButton = ({
  textUpload,
  name,
}: {
  textUpload?: string
  name?: string
}) => {
  return (
    <S.UploadButton>
      {name ? (
        <BaseAvatar alt={name} size={100} />
      ) : (
        <BaseText children={textUpload} fontWeight="semibold" />
      )}
    </S.UploadButton>
  )
}

// helper
const generateUploadFileObj = (data: Partial<UploadFile>): UploadFile => {
  const result: UploadFile = {
    uid: data.uid || '',
    name: data.name || '',
    status: data.status || 'done',
    thumbUrl: data.thumbUrl || '',
  }

  return result
}

interface UploadImageLayoutProps {
  width?: string
  height?: string
  src?: string
  isLoading?: boolean
  circleLayout?: boolean
  showBottomLabel?: boolean
  bottomLabelText?: boolean
  onChange?: (imageFile: File) => void
  styleUpload?: CSSProperties
  textUpload?: string
  alt?: string
}

const UploadImageLayout = (props: UploadImageLayoutProps) => {
  const firstRender = useRef(true)
  const [inPreview, setInPreview] = useState(false)
  const [fileList, setFileList] = useState<UploadFile[]>(
    props.src ? [generateUploadFileObj({ thumbUrl: props.src })] : [],
  )

  useEffect(() => {
    if (props.src && !firstRender.current) {
      const newFile = generateUploadFileObj({ thumbUrl: props.src })

      setTimeout(() => setFileList([newFile]))
    } else {
      firstRender.current = false
    }
  }, [props.src])

  const handleChange: UploadProps['onChange'] = async info => {
    if (info.fileList.length > 0) {
      const base64 =
        (await getImageBase64URL(info.fileList[0].originFileObj)) || ''
      const newFile = generateUploadFileObj({ thumbUrl: base64 })

      setFileList([newFile])

      const emitOnChangeValues = info.fileList[0].originFileObj as File
      props.onChange && props.onChange(emitOnChangeValues)
      return
    }

    setFileList([])
  }

  return (
    <S.RootWrapper>
      <S.UploadWrapper>
        <S.CustomUpload
          listType={props.circleLayout ? 'picture-circle' : 'picture-card'}
          fileList={fileList}
          onChange={handleChange}
          beforeUpload={() => false}
          onPreview={() => setInPreview(true)}
          accept=".png, .jpg"
          maxCount={1}
          $width={props.width}
          $height={props.height}
          style={props?.styleUpload}
        >
          {fileList.length === 0 && (
            <UploadButton textUpload={props?.textUpload} name={props.alt} />
          )}
        </S.CustomUpload>
        {props.isLoading && <Loading isAbsolute />}
      </S.UploadWrapper>
      <BaseImage
        src={fileList?.[0]?.thumbUrl ?? ''}
        preview={{
          visible: inPreview,
          onVisibleChange: visible => setInPreview(visible),
        }}
        style={{ display: 'none' }}
      />

      {props.showBottomLabel && (
        <>
          <SpaceVertical $gap={12} />
          <Text $fontSize="10px" $fontWeight="400" $center>
            {props.bottomLabelText ?? 'Upload your image'}
          </Text>
        </>
      )}
    </S.RootWrapper>
  )
}

export default memo(UploadImageLayout)
